// path: frontend/src/pages/SharePage.tsx
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Copy, ExternalLink, Play } from "lucide-react";

import { API_BASE_URL } from "@/config";
import { SEO } from "@/components/Seo";
import { VideoStructuredData } from "@/components/StructuredData";

interface SocialMetadata {
  title: string;
  description: string;
  hook: string;
  short_caption: string;
  long_caption: string;
  call_to_action: string;
  keywords: string[];
  hashtags: string[];
  slug: string;
  thumbnail_alt: string;
  og_title: string;
  og_description: string;
  content_type: "video" | "article" | "quiz";
}

interface PublicSharePayload {
  id?: string;
  topic?: string;
  language_code?: string;
  video_format?: string;
  video_url?: string | null;
  social_metadata: SocialMetadata;
}

export default function SharePage() {
  const { slug = "" } = useParams();

  const [data, setData] =
    useState<PublicSharePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] =
    useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    async function loadShare(): Promise<void> {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/video/session/share/${encodeURIComponent(slug)}`,
          {
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(
              "This share page is private, unavailable, or no longer exists."
            );
          }

          throw new Error(
            `Unable to load share page (${response.status})`
          );
        }

        const payload =
          (await response.json()) as PublicSharePayload;

        setData(payload);
      } catch (err) {
        if (
          err instanceof DOMException &&
          err.name === "AbortError"
        ) {
          return;
        }

        setError(
          err instanceof Error
            ? err.message
            : "Unable to load share page"
        );
      } finally {
        setLoading(false);
      }
    }

    void loadShare();

    return () => controller.abort();
  }, [slug]);

  const canonical = useMemo(
    () =>
      `https://autoquizai.com/share/${encodeURIComponent(slug)}`,
    [slug]
  );

  const crawlerShareUrl = useMemo(() => {
    const apiBase =
      API_BASE_URL || "http://127.0.0.1:8000";

    return `${apiBase}/share/${encodeURIComponent(slug)}`;
  }, [slug]);

  if (loading) {
    return (
      <main className="max-w-4xl mx-auto p-6">
        <div className="card p-8 text-center">
          Loading shared quiz…
        </div>
      </main>
    );
  }

  if (!data || error) {
    return (
      <main className="max-w-3xl mx-auto p-6">
        <SEO
          title="Share page unavailable"
          description="This AutoQuiz AI share page is unavailable."
          canonical={canonical}
        />

        <div className="card p-8 text-center space-y-4">
          <h1 className="text-2xl font-bold">
            Share page unavailable
          </h1>

          <p className="text-slate-600 dark:text-slate-400">
            {error || "The requested page could not be found."}
          </p>

          <Link
            to="/"
            className="text-blue-600 hover:underline"
          >
            Create your own quiz
          </Link>
        </div>
      </main>
    );
  }

  const social = data.social_metadata;

  const title =
    social.og_title ||
    social.title ||
    data.topic ||
    "AutoQuiz AI Quiz";

  const description =
    social.og_description ||
    social.description ||
    "Educational quiz created with AutoQuiz AI.";

  const shareText =
    social.short_caption ||
    social.description ||
    description;

  const copyShareLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(crawlerShareUrl);
      toast.success("Share link copied");
    } catch {
      toast.error("Could not copy share link");
    }
  };

  const nativeShare = async (): Promise<void> => {
    try {
      if (navigator.share) {
        await navigator.share({
          title,
          text: shareText,
          url: crawlerShareUrl,
        });
      } else {
        await copyShareLink();
      }
    } catch (err) {
      if (
        err instanceof DOMException &&
        err.name === "AbortError"
      ) {
        return;
      }

      toast.error("Unable to share");
    }
  };

  return (
    <main className="max-w-4xl mx-auto p-6 space-y-6">
      <SEO
        title={title}
        description={description}
        keywords={social.keywords.join(", ")}
        ogImageAlt={social.thumbnail_alt}
        canonical={canonical}
        type="video.other"
        videoUrl={data.video_url || undefined}
      />

      <VideoStructuredData
        name={title}
        description={description}
        contentUrl={data.video_url || undefined}
        inLanguage={data.language_code || "en-US"}
      />

      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900/20">
            <Play size={20} />
          </div>

          <h1 className="text-3xl font-bold">
            {title}
          </h1>
        </div>

        <p className="text-slate-600 dark:text-slate-300">
          {description}
        </p>
      </header>

      {data.video_url ? (
        <div className="rounded-xl overflow-hidden bg-black shadow-lg">
          <video
            src={data.video_url}
            controls
            playsInline
            preload="metadata"
            className="w-full"
          />
        </div>
      ) : (
        <div className="card p-6 text-center text-slate-500">
          The quiz is published, but no rendered video is currently available.
        </div>
      )}

      {social.hook && (
        <section className="card p-5">
          <h2 className="font-semibold mb-2">
            Challenge
          </h2>
          <p>{social.hook}</p>
        </section>
      )}

      <section className="card p-5 space-y-4">
        {social.long_caption && (
          <p className="whitespace-pre-wrap">
            {social.long_caption}
          </p>
        )}

        {social.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {social.hashtags.map((hashtag) => (
              <span
                key={hashtag}
                className="text-sm rounded-full border px-3 py-1"
              >
                {hashtag}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => void nativeShare()}
            className="btn bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Share Quiz
          </button>

          <button
            onClick={() => void copyShareLink()}
            className="btn border inline-flex items-center gap-2"
          >
            <Copy size={16} />
            Copy Link
          </button>

          {data.video_url && (
            <a
              href={data.video_url}
              target="_blank"
              rel="noreferrer"
              className="btn border inline-flex items-center gap-2"
            >
              <ExternalLink size={16} />
              Open Video
            </a>
          )}
        </div>
      </section>

      <div className="text-center text-sm text-slate-500">
        Created with{" "}
        <Link
          to="/"
          className="text-indigo-600 hover:underline"
        >
          AutoQuiz AI
        </Link>
      </div>
    </main>
  );
}
